import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  TextField,
  MenuItem,
  Pagination,
  CircularProgress
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ROLES } from '../../constants/roles';
import { useAuth } from '../../contexts/AuthContext';

const STATUS_COLORS = {
  PENDING_VERIFICATION: 'default',
  EMAIL_VERIFIED: 'info',
  ROLE_VERIFIED: 'info',
  ACTIVATED: 'primary',
  PROFILE_COMPLETE: 'success',
  TRAINING_COMPLETE: 'success',
  FULLY_ONBOARDED: 'success',
  REJECTED: 'error',
  CANCELLED: 'warning'
};

const OnboardingDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [hrmsEnabled, setHrmsEnabled] = useState(false);

  const canCreate = useMemo(
    () => [ROLES.SUPER_ADMIN, ROLES.HR_MANAGER].includes(user?.role),
    [user]
  );

  const fetchData = async (pageNumber = 1) => {
    try {
      setLoading(true);
      const params = {
        page: pageNumber,
        limit
      };
      if (statusFilter) params.status = statusFilter;
      if (roleFilter) params.role = roleFilter;
      if (searchTerm.trim()) params.search = searchTerm.trim();
      const response = await axios.get('/api/users/onboard', { params });
      setItems(response.data.items || []);
      setTotal(response.data.total || 0);
      setPage(Number(response.data.page) || pageNumber);
    } catch (error) {
      console.error('Failed to load onboarding requests:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    axios.get('/api/hrms/status')
      .then((res) => setHrmsEnabled(Boolean(res.data?.enabled)))
      .catch(() => setHrmsEnabled(false));
  }, []);

  useEffect(() => {
    fetchData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, roleFilter, searchTerm]);

  const handlePageChange = (_event, value) => {
    setPage(value);
    fetchData(value);
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} mb={3}>
        <Typography variant="h4">{hrmsEnabled ? 'People' : 'User Onboarding'}</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
          label="Search"
          size="small"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          placeholder="Name or email"
          sx={{ minWidth: 220 }}
        />
        <TextField
            select
            label="Status"
            size="small"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All Statuses</MenuItem>
            {Object.keys(STATUS_COLORS).map((status) => (
              <MenuItem key={status} value={status}>{status.replace(/_/g, ' ')}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Role"
            size="small"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All Roles</MenuItem>
            {Object.values(ROLES || {}).map((roleValue) => (
              <MenuItem key={roleValue} value={roleValue}>{roleValue.replace(/_/g, ' ')}</MenuItem>
            ))}
          </TextField>
          {canCreate && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/users/onboard/new')}
            >
              New Onboarding
            </Button>
          )}
        </Stack>
      </Stack>

      <Paper>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Requested By</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(items || []).map((item) => (
                  <TableRow key={item._id} hover>
                    <TableCell>
                      {item.userId?.firstName || item.firstName} {item.userId?.lastName || item.lastName}
                    </TableCell>
                    <TableCell>{item.userId?.email || item.email}</TableCell>
                    <TableCell>
                      <Chip label={item.requestedRole?.replace(/_/g, ' ') || 'N/A'} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.status.replace(/_/g, ' ')}
                        color={STATUS_COLORS[item.status] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{item.requestedBy ? `${item.requestedBy.firstName} ${item.requestedBy.lastName}` : '—'}</TableCell>
                    <TableCell>{new Date(item.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate(`/users/onboard/${item._id}`)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No onboarding requests found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {total > limit && (
        <Box mt={3} display="flex" justifyContent="center">
          <Pagination
            count={Math.ceil(total / limit)}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};

export default OnboardingDashboard;
